import { useState } from "react";

import { ApiError } from "@/api/client";
import { useCancelJob } from "@/api/jobs";
import type { JobStatus } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * JobDetail header affordance for operator-driven abort of a `running`
 * job. Renders nothing outside the `running` state so the parent can
 * drop it in unconditionally — symmetric with `ClearOrphanDraftsBanner`.
 *
 * Click opens a confirmation dialog whose copy explains the
 * drafts-stay-put behaviour (any `tools_draft` rows already written
 * by the run remain — the `ClearOrphanDraftsBanner` self-gates onto
 * the cancelled job once the badge flips). Confirm POSTs to
 * `/api/jobs/:id/cancel` via the `useCancelJob` hook; on success the
 * per-job query invalidates and the header's status badge flips from
 * `running` to `cancelled` without a page reload.
 *
 * 409 path: the orchestrator returns `{ error: "wrong_status",
 * status: <actual> }` when a concurrent sweeper, callback, or second
 * tab moved the job out of `running` between render and click. The
 * mutation's `onError` also invalidates the per-job query so the
 * header re-reads the actual current state; the inline message names
 * that status so the operator understands what happened.
 *
 * See `services/orchestrator/src/routes/jobs-cancel.ts` for the
 * backend contract; filed for issue #215.
 */
export interface AbortRunButtonProps {
  jobId: string;
  status: JobStatus;
}

export function AbortRunButton({ jobId, status }: AbortRunButtonProps) {
  const cancel = useCancelJob();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (status !== "running") return null;

  const handleConfirm = () => {
    cancel.mutate(jobId, {
      onSuccess: () => setConfirmOpen(false),
      onError: () => setConfirmOpen(false),
    });
  };

  const errorMessage = formatCancelError(cancel.error);

  return (
    <>
      <div className="ml-auto flex flex-col items-end gap-1">
        <Button
          type="button"
          variant="destructive"
          size="sm"
          disabled={cancel.isPending}
          onClick={() => setConfirmOpen(true)}
        >
          {cancel.isPending ? "Aborting…" : "Abort run"}
        </Button>
        {errorMessage ? (
          <p className="text-xs text-red-300 font-mono break-all text-right">
            {errorMessage}
          </p>
        ) : null}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Abort this run?</DialogTitle>
            <DialogDescription>
              Aborting will mark the run as cancelled. Any drafts already
              written to{" "}
              <span className="font-mono">tools_draft</span> will remain —
              you can review them or clear via the orphan-drafts tool.
              Proceed?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmOpen(false)}
              disabled={cancel.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleConfirm}
              disabled={cancel.isPending}
            >
              {cancel.isPending ? "Aborting…" : "Confirm abort"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Format a `useCancelJob` failure as a single inline line. The 409
 * branch surfaces the orchestrator-reported current status so the
 * operator knows what state the job actually settled in (a concurrent
 * callback, the wall-clock sweeper, or a second tab beating this click).
 * Anything else falls back to the underlying `Error.message`.
 *
 * Exported for unit testing.
 */
export function formatCancelError(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof ApiError && error.status === 409) {
    const status = readWrongStatus(error.body);
    if (status) {
      return `Job is no longer running (current status: ${status}). Refresh to see the latest state.`;
    }
  }
  if (error instanceof Error) return error.message;
  return String(error);
}

/** Defensive read of the `status` field on the orchestrator's
 * `{ error: "wrong_status", status: <JobStatus> }` 409 body — any
 * unexpected shape returns `null` and the caller falls back to the
 * generic `Error.message`. */
function readWrongStatus(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const status = (body as { status?: unknown }).status;
  return typeof status === "string" ? status : null;
}
